import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type AnyPalette,
  filterPalettes,
  type LibraryFilter,
  type PaletteLibrary,
} from '@/entities/palette';
import { useIsTouchUi } from '@/shared/lib/media';
import { Button, Icons } from '@/shared/ui';

import styles from './ColorPalettes.module.css';
import { usePaletteNames } from './names';
import { type PaletteActions, PaletteRow } from './PaletteRow';

const FILTERS: LibraryFilter[] = ['all', 'system', 'personal', 'workspace', 'favorites'];

/**
 * The palette library of a color picker: a search (name, workspace, or a #hex a palette contains)
 * from the first palette on, scope filters, and a scrolling list whose colors are buttons. A
 * click on a color is the ONLY thing the panel reports: `onPick` is the picker's own callback.
 */
export function LibraryPanel({
  library,
  loading,
  workspaceId,
  actions,
  onPick,
  onCreate,
}: {
  library: PaletteLibrary | undefined;
  loading: boolean;
  workspaceId: string | undefined;
  actions: PaletteActions;
  onPick: (color: string) => void;
  onCreate: () => void;
}) {
  const { t } = useTranslation();
  const touch = useIsTouchUi(); // no unasked on-screen keyboard: the search field is tapped on purpose
  const { systemName } = usePaletteNames();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<LibraryFilter>('all');
  const shown = useMemo(() => {
    if (!library) return [];
    const found = filterPalettes(library, { filter, query, nameOf: systemName });
    // the workspace the picker belongs to comes first among the shared palettes
    const rank = (p: AnyPalette) =>
      p.scope === 'workspace' && p.workspace_id === workspaceId ? 0 : 1;
    return found
      .map((palette, index) => ({ palette, index }))
      .sort((a, b) =>
        a.palette.scope === 'workspace' && b.palette.scope === 'workspace'
          ? rank(a.palette) - rank(b.palette) || a.index - b.index
          : a.index - b.index,
      )
      .map((entry) => entry.palette);
  }, [library, filter, query, workspaceId, systemName]);
  const favorites = new Set(library?.favorite_palettes ?? []);

  return (
    <div className={styles.panel}>
      <div className={styles.search}>
        <input
          type="search"
          value={query}
          aria-label={t('palettes.search')}
          placeholder={t('palettes.search')}
          maxLength={100}
          autoFocus={!touch}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape' && query) {
              event.stopPropagation();
              setQuery('');
            }
          }}
        />
        {query && (
          <button
            type="button"
            className={styles.clear}
            aria-label={t('palettes.clearSearch')}
            onClick={() => {
              setQuery('');
            }}
          >
            <Icons.close />
          </button>
        )}
      </div>
      <div className={styles.chips} role="group" aria-label={t('palettes.filter.label')}>
        {FILTERS.map((value) => (
          <button
            key={value}
            type="button"
            className={styles.chipFilter}
            aria-pressed={filter === value}
            onClick={() => {
              setFilter(value);
            }}
          >
            {t(`palettes.filter.${value}`)}
          </button>
        ))}
      </div>
      {loading && <p className={styles.muted}>{t('palettes.loading')}</p>}
      {!loading && shown.length === 0 && (
        <p className={styles.muted} data-testid="palettes-empty">
          {t(filter === 'favorites' && !query ? 'palettes.emptyFavorites' : 'palettes.empty')}
        </p>
      )}
      <ul className={styles.list} data-testid="palette-list">
        {shown.map((palette) => (
          <PaletteRow
            key={palette.id}
            palette={palette}
            favorite={favorites.has(palette.id)}
            actions={actions}
            onPick={onPick}
            personalIds={library?.personal.map((p) => p.id) ?? []}
          />
        ))}
      </ul>
      <Button size="sm" onClick={onCreate}>
        <Icons.plus /> {t('palettes.create')}
      </Button>
    </div>
  );
}
